/**
 * WorldCupTabBar — three-tab chrome (Picks | Leaderboard | Squads) using shared UnderlineTabBar.
 */

import React, { useMemo } from 'react'
import { Platform, useWindowDimensions } from 'react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { useThemeContext } from '../../../src/theme/ThemeContext'
import { UnderlineTabBar } from '../../ui/UnderlineTabBar'
import { TAB_ICON_SIZE, WORLD_CUP_TABS } from './WorldCupTabBar.constants'
import type { UnderlineTabDef } from '../../ui/UnderlineTabBar'
import type { WorldCupTab } from '../../../src/worldcup/types/worldcup.types'

interface WorldCupTabBarProps {
  activeTab: WorldCupTab
  onTabChange: (tab: WorldCupTab) => void
  /**
   * The tabs to draw. Defaults to all three, so the World Cup and every existing caller are
   * unchanged. The bar renders whatever it is handed and decides nothing: which tabs a
   * competition has earned is the shell's question (AC-11.8), not the chrome's.
   */
  tabs?: readonly UnderlineTabDef<WorldCupTab>[]
}

export const WorldCupTabBar: React.FC<WorldCupTabBarProps> = ({
  activeTab,
  onTabChange,
  tabs = WORLD_CUP_TABS,
}) => {
  const theme = useTheme()
  const { themeMode } = useThemeContext()
  const { width: windowWidth } = useWindowDimensions()
  const isLight = themeMode === 'light'
  const activeTextColor = useMemo(
    () => (isLight ? theme.primary : theme.white),
    [isLight, theme.primary, theme.white],
  )
  const tabBarMaxWidth = Platform.OS !== 'web'
    ? undefined
    : windowWidth >= 1024 ? 880 : windowWidth >= 768 ? 700 : 560

  return (
    <UnderlineTabBar
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={onTabChange}
      activeTextColor={activeTextColor}
      inactiveTextColor={theme.gray[400]}
      underlineColor={theme.primary}
      iconSize={TAB_ICON_SIZE}
      backgroundColor={theme.background.primary}
      includeBottomBorder
      webMaxWidth={tabBarMaxWidth}
      underlineWebGlow
    />
  )
}
